/**
 * 面板 —— 版面里"一块内容"的容器。
 *
 * 方角、粗边、可选一条**反白版头**。版头用反白块而不是"亮色标题文字"：
 * 构成主义最省力的一个动作，一眼就把它和"深色卡片"区分开
 * （`visual-design.md` §3.2、`docs/ui-design.md` §4.3）。
 */

import { Container, Graphics } from 'pixi.js';
import { INK, TRACKING, uiLine, uiPad } from './style';
import { richLabel } from './richText';
import type { Widget } from './base';

export interface PanelSpec {
  readonly scale: number;
  readonly width: number;
  /** 不给就由调用方按内容自己定 —— 面板是最外层容器，高度往往后知后觉 */
  readonly height?: number;
  /** 标题。画成一条**反白**的版头，不是"加粗的标题文字" */
  readonly title?: string;
  readonly fill?: number;
  readonly edge?: number;
  readonly padding?: number;
}

export interface Panel extends Widget {
  /** 内容区。坐标是**相对于内容区原点**的，标题栏的高度已经让开了 */
  readonly body: Container;
  /**
   * 按**内容高度**把面板收紧到刚好包住内容。
   *
   * ⚠️ 这里只收"内容高度"，面板自己加上标题栏和内边距 —— 这个分工是刻意的。
   *
   * 早先暴露的是 `setHeight(总高)`，于是每个调用点都得自己记住
   * "还要加上标题栏那 24px 和内边距"，而**没人记得住**：
   * 房间页上就出现了"面板比内容矮 40px"，内容直接糊到下一个面板上。
   * 把这件事收进面板内部之后，调用方只需要说"我的内容有多高"。
   */
  fitContent(contentHeight: number): void;
}

export function panel(spec: PanelSpec): Panel {
  const pad = uiPad(spec.padding ?? 12, spec.scale);
  const edge = uiLine(1, spec.scale);

  const container = new Container();
  const background = new Graphics();
  container.addChild(background);

  let bodyTop = pad;
  const width = spec.width;

  if (spec.title !== undefined) {
    const headerHeight = uiPad(24, spec.scale);
    const strip = new Graphics().rect(0, 0, width, headerHeight).fill({ color: INK.knockoutFill });
    const header = richLabel({
      text: spec.title.toUpperCase(),
      scale: spec.scale,
      size: 11,
      fill: INK.onKnockout,
      role: 'mono',
      bold: true,
      tracking: TRACKING.wide,
    });
    header.position.set(pad, (headerHeight - header.height) / 2);
    container.addChild(strip, header);
    bodyTop = headerHeight + pad;
  }

  const body = new Container();
  body.position.set(pad, bodyTop);
  container.addChild(body);

  let height = spec.height ?? bodyTop + pad;
  const paint = (): void => {
    background
      .clear()
      .rect(0, 0, width, height)
      .fill({ color: spec.fill ?? INK.panel, alpha: 0.94 })
      .stroke({ color: spec.edge ?? INK.edge, width: edge, alignment: 0 });
  };
  paint();

  return {
    container,
    body,
    width,
    get height() {
      return height;
    },
    fitContent(contentHeight: number) {
      // 内容区从 bodyTop 开始，底下再留一个 pad
      height = Math.round(bodyTop + contentHeight + pad);
      paint();
    },
  };
}
